// @flow
import * as React from 'react';

const { useState, useEffect } = React;

const useDebounceInternal = <T>(value: T, delay: number) => {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue]: [T, Function] = useState<T>(value);

  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler);
      };
    },
    [value, delay] // Only re-call effect if value or delay changes
  );

  return debouncedValue;
};

/*
  useDebounce: hook to track `value` and only update in a
  a debounced fashion. The setter will update `value` immediately
  but debounce updates to `debounced value`

  Returns: [(undebounced) value, debounced value, setter]
*/
export default <T>(initial: T, delay: number): [T, T, Function] => {
  const [v, setV] = useState<T>(initial);
  const dbv = ((useDebounceInternal<T>(v, delay): any): T);
  return [v, dbv, setV];
};
